import { createV3Queries } from "@query/utils/createV3Queries";
import { queriesPayeeEngagements } from "./queriesPayeeEngagements";
import { engagementsApi } from "@api";
import { queriesAuditLogs } from "./queriesAuditLogs";
import { QUERY_EXTENDED_REQUIREMENT_DEFINITIONS } from "../../query/requirementDefinitions/keys";
import { queriesRequirementDefinitions } from "./queriesRequirementDefinitions";

/**
 * Engagement TYPE (template) registry — mirrors `EngagementsApi` 1:1. The
 * name-derived defaults already refresh the sibling engagement reads
 * (`getEngagement` / `listEngagements`) after every write, so the configs below
 * only add the **cross-resource** invalidations the legacy v1 hooks declared
 * (the payee-engagement keys are still v1 caches during coexistence):
 *  - the audit-log activity feed shown on the engagement-type detail screen,
 *  - the payee-engagement lists (a template create/requirement change is reflected
 *    in the per-payee engagement widgets),
 *  - the requirement-definition lists (an engagement's requirement set feeds the
 *    definition's "used by" view).
 *
 * `awaitDependencies` on `getEngagement` reproduces the v1 behavior of refetching
 * the detail before the mutating modal resolves.
 *
 * TODO(v3): drop the payee-engagement / requirement-definition v1 keys once those
 * resources read v3.
 */
export const queriesEngagements = createV3Queries(
  "engagementsApi",
  engagementsApi,
  {
    createEngagement: () => ({
      dependencies: [
        queriesAuditLogs.listAuditLogs.queryKeyBase,
        queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
      ]
    }),
    updateEngagement: queries => ({
      dependencies: [queriesAuditLogs.listAuditLogs.queryKeyBase],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    archiveEngagement: queries => ({
      dependencies: [queriesAuditLogs.listAuditLogs.queryKeyBase],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    restoreEngagement: queries => ({
      dependencies: [queriesAuditLogs.listAuditLogs.queryKeyBase],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    deleteEngagement: () => ({
      dependencies: [queriesAuditLogs.listAuditLogs.queryKeyBase]
    }),
    addEngagementRequirement: queries => ({
      dependencies: [
        queriesAuditLogs.listAuditLogs.queryKeyBase,
        queriesRequirementDefinitions.listRequirementDefinitions.queryKeyBase,
        QUERY_EXTENDED_REQUIREMENT_DEFINITIONS,
        queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
      ],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    }),
    removeEngagementRequirement: queries => ({
      dependencies: [queriesAuditLogs.listAuditLogs.queryKeyBase],
      awaitDependencies: (_res, req) => [
        queries.getEngagement.getQueryKey({ engagementId: req.engagementId })
      ]
    })
  }
);
